import React, { useState, useEffect, useCallback } from "react";
import { Table, Dropdown, Button, Empty, Card, Avatar, Spin, message, Input, Select, Flex, Switch, Tooltip, Modal, Tag, Badge } from "antd";
import { useNavigate } from "react-router-dom";
import { MoreOutlined, DollarOutlined, UserOutlined, LineChartOutlined, TrophyOutlined, SearchOutlined, FilterOutlined, EyeOutlined, ExclamationCircleOutlined, TeamOutlined, FileProtectOutlined, CheckCircleOutlined, ClockCircleOutlined, FileDoneOutlined, StarOutlined } from "@ant-design/icons";
import axios from "axios";
import ComplianceDocViewer from "../../../components/ComplianceDocViewer/ComplianceDocViewer";
import TalentFeedbackModal from "../../../components/TalentFeedbackModal/TalentFeedbackModal";
import { API_CONST } from "../../../const";
import {
  HiredTalentsContainer,
  PageHeader,
  MetricsContainer,
  TabsContainer,
  TableContainer,
  EmptyState,
} from "./HiredTalents.style";

const { Option } = Select;

const HiredTalents = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Active Talents");
  const [searchText, setSearchText] = useState("");
  const [filterRole, setFilterRole] = useState(null);
  const [hiringRecords, setHiringRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myPartnersOnly, setMyPartnersOnly] = useState(
    () => sessionStorage.getItem("hiredTalents_myPartnersOnly") === "true"
  );
  const [activePartnerOrgs, setActivePartnerOrgs] = useState([]);
  const [partnerOrgsLoading, setPartnerOrgsLoading] = useState(false);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [selectedHiringId, setSelectedHiringId] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docViewLoading, setDocViewLoading] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackRecord, setFeedbackRecord] = useState(null);

  const fetchActivePartnerOrgs = useCallback(async () => {
    setPartnerOrgsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_PARTNERS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const relations = response.data.relations || [];
      const orgs = relations
        .filter((r) => r.status === "active" && r.partnerOrganization)
        .map((r) => r.partnerOrganization);
      setActivePartnerOrgs(orgs);
      return orgs;
    } catch (error) {
      console.error("Error fetching partner orgs:", error);
      message.error("Failed to load partner organizations");
      return [];
    } finally {
      setPartnerOrgsLoading(false);
    }
  }, []);

  const fetchHiringRecords = async (partnerOrgs = []) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const body = {};
      if (partnerOrgs.length > 0) {
        body.partnerOrganizations = partnerOrgs;
      }
      const response = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiringRecords(response.data.hiringRecords || []);
    } catch (error) {
      console.error("Error fetching hiring records:", error);
      message.error("Failed to load hiring records");
    } finally {
      setLoading(false);
    }
  };

  const handleMyPartnersToggle = async (checked) => {
    setMyPartnersOnly(checked);
    sessionStorage.setItem("hiredTalents_myPartnersOnly", String(checked));
    if (checked) {
      if (activePartnerOrgs.length === 0) {
        const orgs = await fetchActivePartnerOrgs();
        if (orgs.length === 0) {
          setHiringRecords([]);
          setLoading(false);
        } else {
          fetchHiringRecords(orgs);
        }
      } else {
        fetchHiringRecords(activePartnerOrgs);
      }
    } else {
      fetchHiringRecords([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (sessionStorage.getItem("hiredTalents_myPartnersOnly") === "true") {
        const orgs = await fetchActivePartnerOrgs();
        if (orgs.length === 0) {
          setHiringRecords([]);
          setLoading(false);
        } else {
          fetchHiringRecords(orgs);
        }
      } else {
        fetchHiringRecords([]);
      }
    };
    init();
  }, [fetchActivePartnerOrgs]);

  const activeTalents = hiringRecords.filter((record) => record.status === "active");
  const inactiveTalents = hiringRecords.filter((record) => record.status !== "active");

  const baseData = activeTab === "Active Talents" ? activeTalents : inactiveTalents;

  const currentData = baseData.filter((record) => {
    const matchesSearch = 
      searchText === "" ||
      record.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      record.role?.toLowerCase().includes(searchText.toLowerCase()) ||
      record.email?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesRole = !filterRole || record.role === filterRole;

    return matchesSearch && matchesRole;
  });

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹ ${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹ ${(amount / 100000).toFixed(2)} L`;
    }
    return `₹ ${amount.toLocaleString("en-IN")}`;
  };

  const calculateMetrics = () => {
    const activeMonthlyBilling = activeTalents.reduce(
      (sum, record) => sum + (parseFloat(record.monthlyRate) || 0),
      0
    );
    const avgPerTalent =
      activeTalents.length > 0
        ? activeMonthlyBilling / activeTalents.length
        : 0;
    const totalBilled = activeTalents.reduce(
      (sum, record) => sum + (parseFloat(record.totalBilled) || 0),
      0
    );

    return {
      activeMonthlyBilling,
      avgPerTalent,
      totalBilled,
      activeTalentsCount: activeTalents.length,
    };
  };

  const metrics = calculateMetrics();

  const handleMenuClick = (action, record) => {
    console.log(`${action} for talent:`, record);
    message.info(`${action} action triggered`);
  };

  const openViewDoc = async (doc) => {
    setDocViewLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.COMPLIANCE_DOCUMENTS}/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewingDoc(res.data.document);
    } catch (err) {
      message.error("Failed to load document");
    } finally {
      setDocViewLoading(false);
    }
  };

  const openComplianceDocs = async (record) => {
    setSelectedHiringId(record.id);
    setComplianceModalOpen(true);
    setComplianceLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(
        API_CONST.COMPLIANCE_DOCUMENTS_LIST,
        { hiringRecordId: record.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComplianceDocs(res.data.documents || []);
    } catch (err) {
      message.error("Failed to load compliance documents");
    } finally {
      setComplianceLoading(false);
    }
  };

  const getDaysLeftClass = (days) => {
    if (days < 0) return "inactive";
    if (days <= 30) return "critical";
    if (days <= 90) return "warning";
    return "safe";
  };

  const uniqueRoles = [...new Set(hiringRecords.map(r => r.role).filter(Boolean))];

  const columns = [
    {
      title: "Hiring ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
      render: (id) => (
        <span style={{ color: "#014c75", fontWeight: 500 }}>{id}</span>
      ),
    },
    {
      title: "Job Id",
      dataIndex: "jobId",
      key: "jobId",
      width: "8%",
      render: (jobId) => (
        <a 
          href="#" 
          style={{ color: "#1890ff", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/customer/view-job/${jobId}`);
          }}
        >
          {jobId}
        </a>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "12%",
      render: (text, record) => (
        <a 
          href="#" 
          className="talent-name"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/customer/talent-details/${record.talentId}`);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: "12%",
      render: (text) => <span className="role-text">{text}</span>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: "8%",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Onboarding Date",
      dataIndex: "onboardingDate",
      key: "onboardingDate",
      width: "10%",
      render: (date) => (
        <span className="date-text">
          {date ? new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }) : '-'}
        </span>
      ),
    },
    {
      title: "Contract",
      dataIndex: "contractDuration",
      key: "contractDuration",
      width: "8%",
      render: (duration) => <span className="duration-text">{duration}</span>,
    },
    {
      title: "Days Left",
      dataIndex: "daysLeft",
      key: "daysLeft",
      width: "8%",
      render: (days) => (
        <span className={`days-left ${getDaysLeftClass(days)}`}>
          {days < 0 ? "Completed" : `${days} days`}
        </span>
      ),
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: "10%",
      render: (rate) => (
        <span className="rate-text">₹ {parseFloat(rate || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      title: "Total Billed",
      dataIndex: "totalBilled",
      key: "totalBilled",
      width: "10%",
      render: (amount) => (
        <span className="billed-text" style={{ fontWeight: 600, color: "#014c75" }}>
          ₹ {parseFloat(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "6%",
      align: "center",
      render: (_, record) => (
        <div className="action-buttons">
          <Dropdown
            menu={{
              items: [
                {
                  key: "view",
                  label: "View Details",
                  icon: <EyeOutlined />,
                  onClick: () => navigate(`/customer/talent-details/${record.talentId}`),
                },
                {
                  key: "compliance",
                  label: "Compliance Docs",
                  icon: <FileProtectOutlined />,
                  onClick: () => openComplianceDocs(record),
                },
                {
                  key: "renew",
                  label: "Renew Contract",
                  onClick: () => handleMenuClick("Renew Contract", record),
                },
                {
                  key: "issue",
                  label: "Raise Issue",
                  icon: <ExclamationCircleOutlined />,
                  onClick: () => handleMenuClick("Raise Issue", record),
                },
                ...(record.status === "active" ? [{
                  key: "feedback",
                  label: "Give Feedback",
                  icon: <StarOutlined />,
                  onClick: () => { setFeedbackRecord(record); setFeedbackModalOpen(true); },
                }] : []),
              ],
            }}
            trigger={["click"]}
          >
            <button className="icon-button menu-btn">
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <HiredTalentsContainer>
      <PageHeader>
        <h1>Talents Hired</h1>
        <p>View and manage your hired talent workforce</p>
      </PageHeader>

      <MetricsContainer>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={56}
              icon={<DollarOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#e6fff9" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.activeMonthlyBilling)}</h3>
              <p>Active Monthly Billing</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={56}
              icon={<UserOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#f0f5ff" }}
            />
            <div className="metric-info">
              <h3>{activeTalents.length}</h3>
              <p>Active Talents</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={56}
              icon={<TrophyOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#fff7e6" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.avgPerTalent)}</h3>
              <p>Avg per Talent</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={56}
              icon={<LineChartOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#f6ffed" }}
            />
            <div className="metric-info">
              <h3>{formatCurrency(metrics.totalBilled)}</h3>
              <p>Total Billed</p>
            </div>
          </div>
        </Card>
      </MetricsContainer>

      <TabsContainer>
        <Button
          className={activeTab === "Active Talents" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("Active Talents")}
        >
          Active ({activeTalents.length})
        </Button>
        <Button
          className={activeTab === "Inactive Talents" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("Inactive Talents")}
        >
          Inactive ({inactiveTalents.length})
        </Button>
      </TabsContainer>

      <Flex align="start" justify="space-between" style={{ marginBottom: "20px" }}>
        <Flex gap="middle" align="center" wrap="wrap">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name, role, or email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Role
              </span>
            }
            value={filterRole}
            onChange={setFilterRole}
            style={{ width: 220 }}
            allowClear
          >
            {uniqueRoles.map(role => (
              <Option key={role} value={role}>{role}</Option>
            ))}
          </Select>
          <Tooltip title="Show only talents from your registered partner network">
            <Flex align="center" gap={8} style={{
              background: myPartnersOnly ? "#e6f7ff" : "#fafafa",
              border: `1px solid ${myPartnersOnly ? "#91d5ff" : "#d9d9d9"}`,
              borderRadius: 6,
              padding: "4px 12px",
              cursor: "pointer",
            }}>
              <TeamOutlined style={{ color: myPartnersOnly ? "#1890ff" : "#999" }} />
              <span style={{ fontSize: 13, color: myPartnersOnly ? "#1890ff" : "#595959", userSelect: "none" }}>
                My Partners Only
              </span>
              <Switch
                size="small"
                checked={myPartnersOnly}
                onChange={handleMyPartnersToggle}
                loading={partnerOrgsLoading}
              />
            </Flex>
          </Tooltip>
        </Flex>
      </Flex>

      <TableContainer>
        <Spin spinning={loading}>
          {currentData.length > 0 ? (
            <Table
              columns={columns}
              dataSource={currentData}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} talents`,
              }}
              scroll={{ x: 1400 }}
            />
          ) : (
            <EmptyState>
              <Empty description={loading ? "Loading..." : `No ${activeTab.toLowerCase()} found`} />
            </EmptyState>
          )}
        </Spin>
      </TableContainer>
      <Modal
        open={complianceModalOpen}
        onCancel={() => { setComplianceModalOpen(false); setComplianceDocs([]); setSelectedHiringId(null); }}
        title={
          <span>
            <FileProtectOutlined style={{ marginRight: 8, color: "#014c75" }} />
            Compliance Documents — {selectedHiringId}
          </span>
        }
        footer={null}
        width={720}
      >
        <Spin spinning={complianceLoading}>
          {complianceDocs.length === 0 && !complianceLoading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#999" }}>
              No compliance documents found for this hiring record.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 0" }}>
              {complianceDocs.map((doc) => {
                const DOC_TYPE_LABELS = { nda: "Non-Disclosure Agreement", contract: "Contract Agreement", declaration: "Talent Declaration" };
                const DOC_TYPE_COLORS = { nda: "blue", contract: "purple", declaration: "cyan" };
                const isSigned = doc.status === "signed";
                return (
                  <div
                    key={doc.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 16px",
                      border: `1px solid ${isSigned ? "#b7eb8f" : "#ffe58f"}`,
                      borderRadius: 8,
                      background: isSigned ? "#f6ffed" : "#fffbf0",
                    }}
                  >
                    <div>
                      <Tag color={DOC_TYPE_COLORS[doc.docType]}>{DOC_TYPE_LABELS[doc.docType] || doc.docType}</Tag>
                      {isSigned ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">Signed</Tag>
                      ) : (
                        <Tag icon={<ClockCircleOutlined />} color="warning">Pending Partner Sign-off</Tag>
                      )}
                      {isSigned && doc.signedDate && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: "#666" }}>
                          Signed on {new Date(doc.signedDate).toLocaleDateString("en-GB")}
                        </span>
                      )}
                    </div>
                    {isSigned && (
                      <Button
                        size="small"
                        icon={<FileDoneOutlined />}
                        loading={docViewLoading}
                        onClick={() => openViewDoc(doc)}
                      >
                        Read Document
                      </Button>
                    )}
                  </div>
                );
              })}
              <p style={{ fontSize: 12, color: "#999", marginTop: 8, marginBottom: 0 }}>
                Signed documents are fully accessible once the Partner completes the digital sign-off.
              </p>
            </div>
          )}
        </Spin>
      </Modal>

      {viewingDoc && (
        <ComplianceDocViewer
          document={viewingDoc}
          open={!!viewingDoc}
          onClose={() => setViewingDoc(null)}
          mode="modal"
        />
      )}

      <TalentFeedbackModal
        open={feedbackModalOpen}
        onClose={() => { setFeedbackModalOpen(false); setFeedbackRecord(null); }}
        onSuccess={() => {}}
        hiringRecordId={feedbackRecord?.id}
        talentName={feedbackRecord?.name}
      />
    </HiredTalentsContainer>
  );
};

export default HiredTalents;
